import { useMutation } from '@tanstack/react-query'
import { Loader2, LucideHeart } from 'lucide-react'
import { useState } from 'react'
import { aUser, useAtom } from '~/atoms'
import { ControlTooltip } from '~/components/common'
import { Button, toast } from '~/components/ui'
import type { UserMap } from '~/models'
import { updateMapPreferences } from '~/utils/mapPreferences'
import { getMapHomeViewState } from './mapHomeView'

type MapHomeProps = {
  map: UserMap['initialViewState']
}

async function setMapHome({ map }: MapHomeProps) {
  return updateMapPreferences({ initialViewState: map })
}

export default function SetMapHome({
  map,
}: {
  map: Parameters<typeof getMapHomeViewState>[0]
}) {
  const [user, setUser] = useAtom(aUser)
  const [viewState, setViewState] = useState<
    UserMap['initialViewState'] | undefined
  >()

  const mutation = useMutation({
    mutationFn: setMapHome,
    onSuccess: () => {
      toast({
        duration: 2000,
        variant: 'success',
        title: 'Success',
        description: 'Set current view as map home',
      })

      if (user && viewState) {
        setUser({
          ...user,
          options: {
            ...(user.options || {}),
            map: {
              ...user.options?.map,
              initialViewState: viewState,
            },
          },
        })
      }
    },
    onError: () => {
      toast({
        variant: 'destructive',
        title: 'Error Setting Map Home',
        description:
          'There was a problem saving the current view as map home. Please try again or contact support if the problem persists.',
      })
    },
  })

  if (!user) {
    return null
  }

  return (
    <ControlTooltip
      side="right"
      sideOffset={10}
      text="Set current view as map home">
      <Button
        id="map-set-home"
        disabled={mutation.isPending}
        variant="map"
        size="map"
        className="mb-2 h-7 w-7 bg-background shadow-none"
        onClick={() => {
          const viewState = getMapHomeViewState(map)

          if (!viewState) return

          setViewState(viewState)

          mutation.mutate({
            map: viewState,
          })
        }}>
        {mutation.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <LucideHeart className="h-4 w-4" />
        )}
      </Button>
    </ControlTooltip>
  )
}
